import { FunctionComponent } from 'react';
import CommunityTablePagination from '../community/CommunityTablePagination';
import styled from 'styled-components';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyIcon,
  EmptyText,
  HeaderCell,
  HeaderText,
  Spinner,
  StateIcon,
  StateText,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
  TableState,
} from '../common/AdminTablePrimitives';
import { MentorData } from './MentorManagement';

interface MentorTableProps {
  mentors: MentorData[];
  loading: boolean;
  error: string | null;
  onEdit: (mentorId: number) => void;
  onDelete: (mentorId: number) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const MentorTable: FunctionComponent<MentorTableProps> = ({ mentors, loading, error, onEdit, onDelete, currentPage, totalPages, onPageChange }) => {
  const formatDate = (dateString: string) =>
    new Date(dateString)
      .toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
      .replace(/\. /g, '.')
      .replace(/\.$/, '');

  const formatDepartment = (department: string) => (department && department.length > 20 ? `${department.substring(0, 20)}...` : department || '-');

  const isInitialLoading = loading && mentors.length === 0;
  if (isInitialLoading) {
    return (
      <TableContainer>
        <TableState>
          <Spinner />
          <StateText>멘토 목록을 불러오는 중입니다.</StateText>
        </TableState>
      </TableContainer>
    );
  }

  if (error && mentors.length === 0) {
    return (
      <TableContainer>
        <TableState>
          <StateIcon>안내</StateIcon>
          <StateText>{error}</StateText>
        </TableState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableInner>
        <TableHeader>
          <TableHeaderRow $columns="60px minmax(120px, 2.5fr) minmax(100px, 2fr) minmax(75px, 1.5fr) 100px 110px 110px">
            <HeaderCell><HeaderText>번호</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>멘토</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>소속/직함</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>카테고리</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>상태</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>등록일</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
          </TableHeaderRow>
        </TableHeader>

        <TableBody>
          {mentors.length === 0 ? (
            <TableState>
              <EmptyIcon>∅</EmptyIcon>
              <EmptyText>조건에 맞는 멘토가 없습니다.</EmptyText>
            </TableState>
          ) : (
            mentors.map((mentor, index) => (
              <TableRow key={mentor.id} $columns="60px minmax(120px, 2.5fr) minmax(100px, 2fr) minmax(75px, 1.5fr) 100px 110px 110px">
                <BodyCell>
                  <CellText>{index + 1}</CellText>
                </BodyCell>
                <BodyCell>
                  <MentorInfo>
                    {mentor.image_url && (
                      <MentorAvatar
                        src={mentor.image_url}
                        alt={`${mentor.name} 프로필`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <MentorDetails>
                      <MentorName>{mentor.name}</MentorName>
                      {mentor.email && <MentorEmail>{mentor.email}</MentorEmail>}
                    </MentorDetails>
                  </MentorInfo>
                </BodyCell>
                <BodyCell>
                  <CellText title={mentor.department}>{formatDepartment(mentor.department)}</CellText>
                </BodyCell>
                <BodyCell>
                  <CellText>-</CellText>
                </BodyCell>
                <BodyCell>
                  <StatusText $active={!mentor.is_temp}>{mentor.is_temp ? '임시 저장' : '등록 완료'}</StatusText>
                </BodyCell>
                <BodyCell>
                  <CellText>{formatDate(mentor.created_at)}</CellText>
                </BodyCell>
                <BodyCell>
                  <ActionButtons>
                    <ActionButton type="button" $tone="primary" onClick={() => onEdit(mentor.id)}>
                      수정
                    </ActionButton>
                    <ActionButton type="button" $tone="danger" onClick={() => onDelete(mentor.id)}>
                      삭제
                    </ActionButton>
                  </ActionButtons>
                </BodyCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </TableInner>
      <CommunityTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </TableContainer>
  );
};

const MentorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const MentorAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  object-fit: cover;
  border: 1px solid rgba(112, 115, 124, 0.16);
  flex-shrink: 0;
`;

const MentorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const MentorName = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #171719;
  letter-spacing: 0.01em;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MentorEmail = styled.span`
  font-size: 12px;
  color: rgba(55, 56, 60, 0.61);
  letter-spacing: 0.02em;
  line-height: 133.4%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusText = styled.span<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? '#296768' : 'rgba(55, 56, 60, 0.61)')};
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
`;

export default MentorTable;
